import { observable } from '@legendapp/state';
import { logger } from '@/lib/utils/logger';
import { ConversationResource } from '@/types';
import { storageService } from '@/lib/services';

// Types
interface ConversationMetadata {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface StorageState {
  status: {
    isInitialized: boolean;
    isLoading: boolean;
    error: Error | null;
  };
  conversations: {
    metadata: ConversationMetadata[];
    activeConversation: ConversationResource | null;
    isLoading: boolean;
    error: Error | null;
  };
  credentials: {
    userId: string | null;
    sessionId: string | null;
  };
}

// Initial state
const initialState: StorageState = {
  status: {
    isInitialized: false,
    isLoading: false,
    error: null,
  },
  conversations: {
    metadata: [],
    activeConversation: null,
    isLoading: false,
    error: null,
  },
  credentials: {
    userId: null,
    sessionId: null,
  },
};

// Create the observable store
export const storageState = observable<StorageState>(initialState);

// Action creators
export const storageActions = {
  // Status actions
  setInitialized: (isInitialized: boolean) => {
    logger.debug('[storageStore] Setting initialized state', { isInitialized });
    storageState.status.isInitialized.set(isInitialized);
  },

  startLoading: () => {
    logger.debug('[storageStore] Starting loading');
    storageState.status.isLoading.set(true);
    storageState.status.error.set(null);
  },

  setError: (error: Error) => {
    logger.debug('[storageStore] Setting error', { error });
    storageState.status.isLoading.set(false);
    storageState.status.error.set(error);
  },

  completeLoading: () => {
    logger.debug('[storageStore] Completing loading');
    storageState.status.isLoading.set(false);
  },

  // Conversation actions
  setMetadata: (metadata: ConversationMetadata[]) => {
    logger.debug('[storageStore] Setting conversation metadata', {
      count: metadata.length,
    });
    storageState.conversations.metadata.set(metadata);
  },

  setActiveConversation: (conversation: ConversationResource | null) => {
    logger.debug('[storageStore] Setting active conversation', {
      id: conversation?.id,
    });
    storageState.conversations.activeConversation.set(conversation);
  },

  startConversationLoading: () => {
    logger.debug('[storageStore] Starting conversation loading');
    storageState.conversations.isLoading.set(true);
    storageState.conversations.error.set(null);
  },

  setConversationError: (error: Error) => {
    logger.debug('[storageStore] Setting conversation error', { error });
    storageState.conversations.isLoading.set(false);
    storageState.conversations.error.set(error);
  },

  completeConversationLoading: () => {
    logger.debug('[storageStore] Completing conversation loading');
    storageState.conversations.isLoading.set(false);
  },

  // Credential actions
  setCredentials: (userId: string | null, sessionId: string | null) => {
    logger.debug('[storageStore] Setting credentials');
    storageState.credentials.userId.set(userId);
    storageState.credentials.sessionId.set(sessionId);
  },

  reset: () => {
    logger.debug('[storageStore] Resetting state');
    storageState.set(initialState);
  },

  // Service-connected actions
  initialize: async (userId: string, sessionId: string) => {
    try {
      storageActions.startLoading();
      await storageService.initialize(userId, sessionId);
      storageActions.setInitialized(true);
      storageActions.setCredentials(userId, sessionId);
    } catch (error) {
      logger.error('[storageStore] Failed to initialize:', error);
      storageActions.setError(error as Error);
      throw error;
    } finally {
      storageActions.completeLoading();
    }
  },

  storeConversation: async (conversation: ConversationResource) => {
    try {
      storageActions.startConversationLoading();
      await storageService.storeConversation(conversation);
      storageActions.setActiveConversation(conversation);
    } catch (error) {
      logger.error('[storageStore] Failed to store conversation:', error);
      storageActions.setConversationError(error as Error);
      throw error;
    } finally {
      storageActions.completeConversationLoading();
    }
  },

  getConversation: async (id: string) => {
    try {
      storageActions.startConversationLoading();
      const conversation = await storageService.getConversation(id);
      storageActions.setActiveConversation(conversation);
      return conversation;
    } catch (error) {
      logger.error('[storageStore] Failed to get conversation:', error);
      storageActions.setConversationError(error as Error);
      throw error;
    } finally {
      storageActions.completeConversationLoading();
    }
  },

  listConversations: async (limit?: number, offset?: number) => {
    try {
      storageActions.startConversationLoading();
      const metadata = await storageService.listConversations(limit, offset);
      storageActions.setMetadata(metadata);
      return metadata;
    } catch (error) {
      logger.error('[storageStore] Failed to list conversations:', error);
      storageActions.setConversationError(error as Error);
      throw error;
    } finally {
      storageActions.completeConversationLoading();
    }
  },

  deleteConversation: async (id: string) => {
    try {
      storageActions.startConversationLoading();
      await storageService.deleteConversation(id);
      // Remove from metadata
      const currentMetadata = storageState.conversations.metadata.get();
      storageActions.setMetadata(currentMetadata.filter((m) => m.id !== id));
      // Clear active conversation if it was the deleted one
      const activeConversation =
        storageState.conversations.activeConversation.get();
      if (activeConversation?.id === id) {
        storageActions.setActiveConversation(null);
      }
    } catch (error) {
      logger.error('[storageStore] Failed to delete conversation:', error);
      storageActions.setConversationError(error as Error);
      throw error;
    } finally {
      storageActions.completeConversationLoading();
    }
  },
};

// Selectors
export const storageSelectors = {
  isInitialized: () => storageState.status.isInitialized.get(),
  isLoading: () => storageState.status.isLoading.get(),
  getError: () => storageState.status.error.get(),
  getMetadata: () => storageState.conversations.metadata.get(),
  getActiveConversation: () =>
    storageState.conversations.activeConversation.get(),
  isConversationLoading: () => storageState.conversations.isLoading.get(),
  getConversationError: () => storageState.conversations.error.get(),
  getCredentials: () => ({
    userId: storageState.credentials.userId.get(),
    sessionId: storageState.credentials.sessionId.get(),
  }),
};
